/**
 * Feature Settings Tab
 * Main component for managing hotel feature modules (Owner/Manager only)
 * Allows toggling: PMS, AI Agents, Token Economy, Web3 features
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Save, RefreshCw, AlertCircle, ArrowRight } from 'lucide-react';
import { ModuleCard } from './components/ModuleCard';
import { PricingSummaryCard } from './components/PricingSummaryCard';
import { ValidationErrorsAlert } from './components/ValidationErrorsAlert';
import { AiIntegrationPanel } from './components/AiIntegrationPanel';
import { DisableModuleDialog } from './components/DisableModuleDialog';
import { ModuleSection } from './components/ModuleSection';
import { useHotelFeatures } from './hooks/useHotelFeatures';
import { getIcon } from './utils/iconMapper';

export function FeatureSettingsTab({ hotelId, currentUser }) {
  const {
    config,
    pricing,
    validationErrors,
    metadata,
    loading,
    saving,
    error,
    toggleFeature,
    saveFeatures,
    reload
  } = useHotelFeatures(hotelId);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [localConfig, setLocalConfig] = useState(null);
  const [disableDialog, setDisableDialog] = useState({ open: false, module: null, affected: [] });

  // Check authorization
  const isAuthorized = currentUser && (currentUser.role === 'owner' || currentUser.role === 'manager');

  // Sync local config with fetched config
  useEffect(() => {
    if (config) {
      setLocalConfig(config);
    }
  }, [config]);

  // Track changes
  useEffect(() => {
    setHasUnsavedChanges(false);
  }, [config]);

  const handleToggle = (featureKey, value) => {
    setLocalConfig(prev => ({
      ...prev,
      [featureKey]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleUpdateConfig = (updatedConfig) => {
    setLocalConfig(updatedConfig);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    // Check if disabling PMS
    if (config?.pms_enabled && !localConfig?.pms_enabled) {
      setDisableDialog({
        open: true,
        module: 'PMS (Property Management System)',
        affected: [
          'Room Management',
          'Bookings',
          'Room Board',
          'Housekeeping',
          'Availability Calendar',
          'Pricing & Discounts',
          'Meeting Rooms'
        ]
      });
      return;
    }
    
    // Check if disabling any AI agent while HERMES is enabled
    const hermesEnabled = config?.hermes_enabled;
    const allAiCurrentlyEnabled = config?.ai_bitsy_enabled && config?.ai_marina_enabled && config?.ai_sage_enabled;
    const allAiWillBeEnabled = localConfig?.ai_bitsy_enabled && localConfig?.ai_marina_enabled && localConfig?.ai_sage_enabled;
    
    if (hermesEnabled && allAiCurrentlyEnabled && !allAiWillBeEnabled) {
      // User is trying to disable an AI agent while HERMES is active
      const disabledAgents = [];
      if (config?.ai_bitsy_enabled && !localConfig?.ai_bitsy_enabled) disabledAgents.push('Bitsy');
      if (config?.ai_marina_enabled && !localConfig?.ai_marina_enabled) disabledAgents.push('Marina');
      if (config?.ai_sage_enabled && !localConfig?.ai_sage_enabled) disabledAgents.push('Sage');
      
      setDisableDialog({
        open: true,
        module: `${disabledAgents.join(', ')} AI Agent${disabledAgents.length > 1 ? 's' : ''}`,
        affected: [
          'HERMES Token System will be disabled',
          'Staff token rewards will stop',
          'Guest delight multipliers will stop',
          'Token buy-back & burn mechanism will pause',
          'All AI-dependent features will be affected'
        ]
      });
      return;
    }
    
    // Proceed with save
    try {
      await saveFeatures(localConfig);
      setHasUnsavedChanges(false);
    } catch (err) {
      // Error handled in hook
    }
  };

  const handleConfirmDisable = async () => {
    setDisableDialog({ open: false, module: null, affected: [] });
    try {
      await saveFeatures(localConfig);
      setHasUnsavedChanges(false);
    } catch (err) {
      // Error handled in hook
    }
  };

  const handleDiscard = () => {
    reload();
    setHasUnsavedChanges(false);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="feature-settings-loading">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <div>
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load feature settings. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  // Authorization guard
  if (!isAuthorized) {
    return (
      <div className="space-y-6" data-testid="feature-settings-unauthorized">
        <div>
          <h2 className="text-2xl font-bold heading-font mb-2">Feature Settings</h2>
          <p className="text-muted-foreground">
            Configure hotel modules and integrations
          </p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Access Restricted:</strong> Only hotel owners and managers can access Feature Settings.
            Please contact your hotel administrator for assistance.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feature-settings-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold heading-font mb-2">Feature Settings</h2>
        <p className="text-muted-foreground">
          Choose which modules to enable for your hotel. Configure your perfect solution.
        </p>
      </div>

      {/* Validation Errors */}
      {validationErrors && Object.keys(validationErrors).length > 0 && (
        <ValidationErrorsAlert errors={validationErrors} />
      )}

      {/* Main Layout: Modules + Pricing Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Module Cards - Metadata Driven */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Render sections from metadata */}
          {metadata && (
            <>
              {/* Core Systems */}
              {metadata.core && (
                <>
                  <ModuleSection
                    title="Core Systems"
                    description="Essential property management and operations"
                    modules={metadata.core}
                    config={localConfig}
                    onToggle={handleToggle}
                    metadata={metadata.core}
                    testIdPrefix="module"
                  />
                  <Separator />
                </>
              )}

              {/* AI Agents */}
              {metadata.ai && (
                <>
                  <section>
                    <div className="mb-4">
                      <h3 className="text-lg heading-font tracking-tight mb-1">AI Agents</h3>
                      <p className="text-sm text-muted-foreground">
                        Intelligent automation for guest experience and operations
                      </p>
                    </div>
                    <div className="space-y-4">
                      {metadata.ai.map(module => {
                        const Icon = getIcon(module.id);
                        return (
                          <ModuleCard
                            key={module.id}
                            title={module.name}
                            description={module.description}
                            icon={Icon}
                            enabled={localConfig?.[`${module.id}_enabled`]}
                            onToggle={(val) => handleToggle(`${module.id}_enabled`, val)}
                            pricingImpact={module.monthly_cost}
                            testId={`module-${module.id}`}
                          />
                        );
                      })}
                    </div>
                    
                    {/* AI Integration Configuration */}
                    {(localConfig?.ai_bitsy_enabled || localConfig?.ai_marina_enabled || localConfig?.ai_sage_enabled) && (
                      <div className="mt-4">
                        <AiIntegrationPanel
                          config={localConfig}
                          onUpdateConfig={handleUpdateConfig}
                          aiAgentsEnabled={true}
                        />
                      </div>
                    )}
                  </section>
                  <Separator />
                </>
              )}

              {/* Token Economy */}
              {metadata.token_economy && (
                <>
                  <ModuleSection
                    title="Token Economy"
                    description="Advanced gamification and incentive system"
                    modules={metadata.token_economy}
                    config={localConfig}
                    onToggle={handleToggle}
                    metadata={metadata.token_economy}
                    testIdPrefix="module"
                  />
                  <Separator />
                </>
              )}

              {/* Web3 Wallet Setup Notice */}
              {!config?.wallet_configured && (
                <>
                  <Alert data-testid="wallet-setup-notice">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Web3 Features Disabled:</strong> To enable NFT Receipts, Identity Tokens, and Crypto Payments, please configure your wallet addresses in{' '}
                      <Button
                        variant="link"
                        className="p-0 h-auto font-semibold"
                        onClick={() => {
                          const billingTab = document.querySelector('[data-testid="owner-nav-billing-link"]');
                          if (billingTab) billingTab.click();
                        }}
                      >
                        Finance & Billing → Wallet Addresses
                      </Button>
                    </AlertDescription>
                  </Alert>
                  <Separator />
                </>
              )}

              {/* Web3 Features */}
              {metadata.web3 && (
                <ModuleSection
                  title="Web3 Features"
                  description="Blockchain integrations for payments and identity"
                  modules={metadata.web3}
                  config={localConfig}
                  onToggle={handleToggle}
                  metadata={metadata.web3}
                  testIdPrefix="module"
                />
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleDiscard}
              disabled={!hasUnsavedChanges || saving}
              data-testid="discard-changes-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Discard Changes
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || saving}
              data-testid="save-features-button"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Right: Pricing Summary */}
        <div>
          <PricingSummaryCard 
            pricing={pricing} 
            hasUnsavedChanges={hasUnsavedChanges} 
          />
        </div>
      </div>

      {/* Disable Module Confirmation Dialog */}
      <DisableModuleDialog
        open={disableDialog.open}
        onOpenChange={(open) => setDisableDialog({ ...disableDialog, open })}
        moduleName={disableDialog.module}
        affectedFeatures={disableDialog.affected}
        onConfirm={handleConfirmDisable}
      />
    </div>
  );
}
